import * as React from 'react'

interface TooltipProviderProps {
    children: React.ReactNode
    delayDuration?: number
}

interface TooltipProps {
    children: React.ReactNode
    delayDuration?: number
}

interface TooltipTriggerProps {
    children: React.ReactElement
    asChild?: boolean
}

interface TooltipContentProps {
    children: React.ReactNode
    side?: 'top' | 'right' | 'bottom' | 'left'
    sideOffset?: number
    className?: string
}

const TooltipContext = React.createContext<{
    open: boolean
    setOpen: (v: boolean) => void
    triggerRef: React.RefObject<HTMLElement | null>
    delay: number
}>({
    open: false,
    setOpen: () => { },
    triggerRef: { current: null },
    delay: 0,
})

function TooltipProvider({ children }: TooltipProviderProps) {
    return <>{children}</>
}

function Tooltip({ children, delayDuration = 0 }: TooltipProps) {
    const [open, setOpen] = React.useState(false)
    const triggerRef = React.useRef<HTMLElement | null>(null)

    return (
        <TooltipContext.Provider value={{ open, setOpen, triggerRef, delay: delayDuration }}>
            <div className="relative w-full">{children}</div>
        </TooltipContext.Provider>
    )
}

function TooltipTrigger({ children, asChild }: TooltipTriggerProps) {
    const { setOpen, triggerRef, delay } = React.useContext(TooltipContext)
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>()

    const handleMouseEnter = () => {
        if (delay > 0) {
            timeoutRef.current = setTimeout(() => setOpen(true), delay)
        } else {
            setOpen(true)
        }
    }

    const handleMouseLeave = () => {
        clearTimeout(timeoutRef.current)
        setOpen(false)
    }

    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(children as React.ReactElement<any>, {
            ref: triggerRef,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onFocus: () => setOpen(true),
            onBlur: () => setOpen(false),
        })
    }

    return (
        <span
            ref={triggerRef as React.RefObject<HTMLSpanElement>}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
        >
            {children}
        </span>
    )
}

function TooltipContent({ children, side = 'top', sideOffset = 4, className = '' }: TooltipContentProps) {
    const { open } = React.useContext(TooltipContext)

    if (!open) return null

    const positions: Record<string, string> = {
        top: `bottom-full left-1/2 -translate-x-1/2 mb-${sideOffset / 4}`,
        bottom: `top-full left-1/2 -translate-x-1/2 mt-${sideOffset / 4}`,
        left: `right-full top-1/2 -translate-y-1/2 mr-${sideOffset / 4}`,
        right: `left-full top-1/2 -translate-y-1/2`,
    }

    return (
        <div
            className={`absolute ${positions[side]} z-50 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-md animate-in fade-in-0 ${className}`}
            style={side === 'right' ? { marginLeft: `${sideOffset}px` } : undefined}
            role="tooltip"
        >
            {children}
        </div>
    )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
